/* SyncStatusBar — last-synced timestamp + "Refresh now" button.
 *
 * Shows a compact bar above the Live Ticker / Cost dashboards listing
 * every data source feeding the page with:
 *   - last_synced_at timestamp ("4m ago", "2h ago", etc.)
 *   - live/manual badge
 *   - global "Refresh now" button (CEO + leader + sub-leader + cost_controller)
 *
 * Polls /api/daily-sales/sync-status/{division_id} every 30s.
 * Refresh button → POST /api/daily-sales/sync-now/{division_id} +
 * optimistic UI feedback ("Syncing…" spinner) + auto re-poll until
 * a fresh timestamp lands.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { RefreshCw, Activity, Clock, AlertCircle, CheckCircle } from 'lucide-react';

const POLL_INTERVAL_MS = 30000;


function formatAge(seconds) {
  if (seconds === null || seconds === undefined) return 'never';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}


function ageColor(seconds, isLive) {
  if (seconds === null || seconds === undefined) return '#9CA3AF';
  if (isLive) {
    // For live sources, fresher is better
    if (seconds < 120) return '#10B981';      // <2 min · green
    if (seconds < 600) return '#F59E0B';      // <10 min · amber
    return '#DC2626';                          // ≥10 min · red
  }
  // Manual sources — month-old data is normal
  if (seconds < 7 * 24 * 3600) return '#6B7280';
  return '#9CA3AF';
}


export default function SyncStatusBar({ divisionId = 'me' }) {
  const [status, setStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastTriggeredAt, setLastTriggeredAt] = useState(null);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get(`/daily-sales/sync-status/${divisionId}`);
      setStatus(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load sync status');
    }
  }, [divisionId]);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const triggerRefresh = useCallback(async () => {
    setRefreshing(true);
    setLastTriggeredAt(new Date());
    try {
      await api.post(`/daily-sales/sync-now/${divisionId}`);
      // Poll every 4s until the timestamp is newer than the trigger time
      const triggerMs = Date.now();
      let attempts = 0;
      const t = setInterval(async () => {
        attempts++;
        await fetchStatus();
        const fresh = (status?.sources || []).find(s =>
          s.id === 'ezee_sales' &&
          s.last_synced_at &&
          new Date(s.last_synced_at).getTime() > triggerMs
        );
        if (fresh || attempts >= 12) {
          clearInterval(t);
          setRefreshing(false);
        }
      }, 4000);
    } catch (e) {
      setRefreshing(false);
      setError(e?.response?.data?.detail || 'Refresh failed');
    }
  }, [divisionId, fetchStatus, status]);

  if (!status) return null;
  const sources = status.sources || [];
  if (!sources.length) return null;

  const liveSources = sources.filter(s => s.live);
  const manualSources = sources.filter(s => !s.live);

  return (
    <div
      className="rounded-xl bg-white p-3 mb-4 flex items-center gap-3 flex-wrap"
      style={{ border: '1px solid #E5E1DC' }}
      data-testid="sync-status-bar"
    >
      <div className="flex items-center gap-1.5">
        <Activity size={14} color="#1B2A4A" />
        <span className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#1B2A4A' }}>
          Data Sources
        </span>
      </div>

      {/* Live sources */}
      <div className="flex items-center gap-2 flex-wrap flex-1">
        {liveSources.map(s => (
          <div
            key={s.id}
            data-testid={`sync-source-${s.id}`}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md"
            style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}
            title={`Last fetched ${s.items_count || 0} items in ${s.elapsed_ms || '?'}ms`}
          >
            <CheckCircle size={11} color="#10B981" />
            <span className="text-[11px]" style={{ color: '#1B2A4A' }}>
              <strong>{s.label}</strong>
            </span>
            <span
              className="text-[10px] tabular-nums"
              style={{ color: ageColor(s.age_seconds, true) }}
            >
              {formatAge(s.age_seconds)}
            </span>
            {s.in_flight && (
              <span className="text-[9px] px-1 rounded-full" style={{ background: '#3B82F6', color: 'white' }}>
                syncing…
              </span>
            )}
          </div>
        ))}
        {manualSources.map(s => (
          <div
            key={s.id}
            data-testid={`sync-source-${s.id}`}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md"
            style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}
            title={`Last entered by ${s.last_entered_by || 'unknown'} on ${s.covers_through || '?'}`}
          >
            <Clock size={11} color="#6B7280" />
            <span className="text-[11px]" style={{ color: '#1B2A4A' }}>
              <strong>{s.label}</strong>
            </span>
            <span className="text-[10px] tabular-nums" style={{ color: '#6B7280' }}>
              {s.covers_through ? `through ${s.covers_through}` : formatAge(s.age_seconds)}
            </span>
            <span className="text-[9px] uppercase tracking-wider px-1 rounded-full" style={{ background: '#FEF3C7', color: '#92400E' }}>
              manual
            </span>
          </div>
        ))}
      </div>

      {/* Refresh button */}
      <button
        onClick={triggerRefresh}
        disabled={refreshing}
        data-testid="sync-refresh-btn"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all"
        style={{
          background: refreshing ? '#E5E7EB' : '#1B2A4A',
          color: refreshing ? '#6B7280' : 'white',
          border: '1px solid #1B2A4A',
          cursor: refreshing ? 'not-allowed' : 'pointer',
        }}
      >
        <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
        {refreshing ? 'Syncing…' : 'Refresh now'}
      </button>

      {error && (
        <div className="w-full flex items-center gap-1 mt-2 text-[11px]" style={{ color: '#DC2626' }}>
          <AlertCircle size={11} />
          {error}
        </div>
      )}
    </div>
  );
}
